<template>
  <div class="meta-block">
    <h4 class="meta-subtitle">Reference drawing</h4>
    <v-select
      class="reference-select"
      :value="selectedDocumentId"
      :items="documents"
      item-text="name"
      item-value="id"
      label="ISO drawing / reference file"
      placeholder="Select a reference file"
      no-data-text="No tag or task documents"
      dense
      outlined
      hide-details
      :loading="drawing.loading"
      :disabled="drawing.loading || !selectableDocumentCount"
      :menu-props="{ offsetY: true, maxHeight: 320 }"
      @change="$emit('select', $event)"
    >
      <template #item="{ item, attrs, on }">
        <v-list-item
          v-bind="attrs"
          class="reference-option"
          v-on="on"
        >
          <v-list-item-content>
            <v-list-item-title
              :id="attrs['aria-labelledby']"
              class="reference-option__name"
            >
              {{ item.name }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="item.subtitle">
              {{ item.subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-chip
              x-small
              outlined
              :color="item.isIso ? 'primary' : 'grey darken-1'"
              class="reference-type-chip"
            >
              {{ item.type }}
            </v-chip>
          </v-list-item-action>
        </v-list-item>
      </template>
      <template #selection="{ item }">
        <div class="reference-selection">
          <span class="reference-selection__name">{{ item.name }}</span>
          <v-chip
            x-small
            outlined
            :color="item.isIso ? 'primary' : 'grey darken-1'"
            class="reference-type-chip"
          >
            {{ item.type }}
          </v-chip>
        </div>
      </template>
    </v-select>
    <div
      class="drawing-thumb"
      :class="{ 'drawing-thumb--unavailable': !drawing.available }"
    >
      <div v-if="drawing.loading" class="drawing-state" role="status">
        <v-progress-circular indeterminate size="22" width="2" color="primary" />
        <span>Loading linked drawing…</span>
      </div>
      <img
        v-else-if="drawing.thumbnailUrl && !thumbnailFailed"
        :src="drawing.thumbnailUrl"
        :alt="`${drawing.name} preview`"
        @error="thumbnailFailed = true"
      />
      <div v-else class="drawing-state">
        <v-icon size="28" color="grey darken-1">mdi-file-document-outline</v-icon>
        <span>{{ stateText }}</span>
      </div>

      <div class="drawing-footer">
        <div class="drawing-copy">
          <span class="drawing-name">{{ drawing.name }}</span>
          <span v-if="drawing.revision" class="drawing-revision">
            {{ drawing.revision }}
          </span>
        </div>
        <button
          type="button"
          class="drawing-expand"
          :disabled="!drawing.canExpand"
          :aria-label="expandLabel"
          :title="expandLabel"
          @click="onExpand"
        >
          <v-icon size="15">mdi-arrow-expand-all</v-icon>
        </button>
      </div>
    </div>
    <p v-if="drawing.error" class="drawing-error" role="alert">
      {{ drawing.error }}
    </p>
    <p v-if="popoutError" class="drawing-error" role="alert">
      {{ popoutError }}
    </p>
  </div>
</template>

<script>
/**
 * Reference-drawing picker and preview.
 *
 * EXPAND REPLACES THE IN-APP PANEL WITH A BROWSER WINDOW — the drawing is meant
 * to live on a second monitor while the workspace keeps the 3D view. The in-app
 * `FloatingDocumentViewer` is the FALLBACK, not a companion: it is opened (the
 * `expand` event) only when the pop-out cannot serve — a blocked pop-up, or an
 * Office file that only the in-app viewer knows how to hand to Office Online.
 * One control, two outcomes, never a dead end.
 *
 * REPLACES IS LOAD-BEARING, AND THE SUCCESS PATH NEEDS AN EMIT OF ITS OWN. The
 * panel is owned by `views/index.vue` (`activeDrawing`), which nothing in this
 * component can reach; once opened it survives every later expand until the
 * parent is told to clear it. Without the `popped-out` emit, a panel opened by
 * an EARLIER fallback stays on screen showing a DIFFERENT drawing than the
 * pop-out — expand a .docx (panel), then expand a PDF (pop-out), and the
 * conformance record shows two unrelated reference drawings at once. The emit
 * is not redundant with `openDrawingPopout` returning a window; it is the only
 * thing that dismisses the stale panel.
 *
 * `onExpand` MUST STAY SYNCHRONOUS. Chrome only honours `window.open` inside
 * the user gesture, so a single `await` anywhere above `openDrawingPopout`
 * turns every expand into a blocked pop-up. See `utils/drawingPopout.js`.
 */
import {
  isPopoutRenderable,
  openDrawingPopout,
} from '@/features/Verification/utils/drawingPopout';

export default {
  name: 'DrawingThumbnail',
  props: {
    drawing: {
      type: Object,
      required: true,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    selectedDocumentId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      thumbnailFailed: false,
      // Rendered in the same `.drawing-error` slot the load error uses. There
      // is no toast infrastructure in this app (`$message` exists only as a
      // defensive no-op in PotreeMeasurements), and a blocked pop-up must be
      // explained or the fallback panel looks like the button misfired.
      popoutError: '',
    };
  },
  computed: {
    selectableDocumentCount() {
      return this.documents.filter((item) => item && item.id).length;
    },
    stateText() {
      if (this.drawing.error) return 'Drawing preview unavailable';
      if (!this.drawing.available && this.drawing.hasOptions) {
        return 'Select a reference file';
      }
      if (!this.drawing.available) return 'No related documents';
      return 'Preview not available';
    },
    expandLabel() {
      return this.drawing.canExpand
        ? `Open ${this.drawing.name} in a new window`
        : 'No reference file available to expand';
    },
  },
  watch: {
    'drawing.thumbnailUrl'() {
      this.thumbnailFailed = false;
    },
    // Keyed on the selection rather than the thumbnail: two references can both
    // have no thumbnail, and a stale "Pop-up blocked" line hanging under a
    // different drawing reads as a fresh failure.
    selectedDocumentId() {
      this.popoutError = '';
    },
  },
  methods: {
    onExpand() {
      if (!this.drawing.canExpand || !this.drawing.document) return;
      const doc = this.drawing.document;
      this.popoutError = '';

      // Office and anything else the pop-out cannot render goes straight to the
      // in-app viewer, which owns the Office Online handoff. No error: this is
      // a supported outcome, not a failure.
      if (!isPopoutRenderable(doc)) {
        this.$emit('expand', doc);
        return;
      }

      // Synchronous by contract — nothing may be awaited before this call.
      if (!openDrawingPopout(doc)) {
        this.popoutError = 'Pop-up blocked — opened in the panel instead. '
          + 'Allow pop-ups for this site to use a second screen.';
        this.$emit('expand', doc);
        return;
      }

      // SUCCESS — dismiss the in-app panel. "Expand REPLACES the panel" is the
      // ratified behaviour, and the panel can only be holding a drawing from an
      // earlier fallback (Office file, or a pop-up that was blocked at the
      // time). Leaving it up puts a second, different drawing beside the one in
      // the pop-out. Harmless no-op when no panel is open.
      this.$emit('popped-out', doc);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.meta-block {
  padding: 14px 0;
  border-bottom: 1px dashed $vf-line-soft;
}

.meta-subtitle {
  font-size: 11px;
  font-family: $vf-font-mono;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $vf-ink-3;
  font-weight: 500;
  margin: 0 0 10px;
}

.reference-select {
  margin-bottom: 10px;

  ::v-deep .v-input__slot {
    min-height: 38px !important;
    background: #fff;
  }

  ::v-deep .v-label {
    font-family: $vf-font-body;
    font-size: 12px;
  }
}

.reference-selection {
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
}

.reference-selection__name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: $vf-font-mono;
  font-size: 10px;
}

.reference-option {
  min-height: 48px;
}

.reference-option__name {
  font-family: $vf-font-mono;
  font-size: 11px;
}

.reference-type-chip {
  flex: 0 0 auto;
  max-width: 92px;

  ::v-deep .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.drawing-thumb {
  border: 1px solid $vf-line;
  border-radius: $vf-r-sm;
  background: #f6f8f7;
  height: 130px;
  overflow: hidden;
  position: relative;

  > img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.drawing-thumb--unavailable {
  background: #f5f5f5;
}

.drawing-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: $vf-ink-3;
  font-size: 11px;
}

.drawing-footer {
  position: absolute;
  inset: auto 0 0;
  min-height: 34px;
  padding: 6px 7px;
  background: rgba(255, 255, 255, 0.94);
  border-top: 1px solid $vf-line-soft;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.drawing-copy {
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.drawing-name,
.drawing-revision {
  font-family: $vf-font-mono;
  font-size: 10px;
  color: $vf-ink-2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawing-revision {
  color: $vf-ink-3;
  flex: 0 0 auto;
}

.drawing-expand {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  background: #fff;
  color: $vf-ink;
  border: 1px solid $vf-line;
  border-radius: 3px;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: $vf-primary;
    color: $vf-primary;
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
}

.drawing-error {
  margin: 6px 0 0;
  color: $vf-error;
  font-size: 10px;
  line-height: 1.4;
}
</style>
